import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { getAvailableSlots, getPublicServices, getPublicBookingStaff, publicBookAppointment } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Calendar, Clock, User, Phone, Mail, Check, ChevronLeft, Scissors } from 'lucide-react';

const TOTAL_STEPS = 5;

export default function PublicBooking() {
  const [step, setStep] = useState(1);
  const [staffList, setStaffList] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [bookingComplete, setBookingComplete] = useState(false);

  const [services, setServices] = useState([]);
  const [selectedServiceIds, setSelectedServiceIds] = useState([]);

  const [formData, setFormData] = useState({
    first_name: '',
    paternal_surname: '',
    maternal_surname: '',
    phone: '',
    email: '',
    reason: '',
  });

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
    setFormData({ ...formData, phone: value });
  };

  const getDates = () => {
    const dates = [];
    const today = new Date();
    for (let i = 0; i < 14; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const formatDate = (date) => date.toISOString().split('T')[0];

  const formatDisplayDate = (date) =>
    date.toLocaleDateString('es-MX', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });

  const bookableServices = useMemo(() => {
    if (!selectedStaff) return [];
    const ids = selectedStaff.service_ids;
    if (!Array.isArray(ids) || ids.length === 0) return services;
    const idSet = new Set(ids.map(Number));
    return services.filter((s) => idSet.has(Number(s.id)));
  }, [services, selectedStaff]);

  const totalDurationMinutes = useMemo(() => {
    if (!Array.isArray(selectedServiceIds) || selectedServiceIds.length === 0) return 0;
    return selectedServiceIds.reduce((sum, id) => {
      const svc = services.find((s) => Number(s.id) === Number(id));
      return sum + (svc?.duration_minutes ? Number(svc.duration_minutes) : 0);
    }, 0);
  }, [selectedServiceIds, services]);

  const fetchSlots = useCallback(async (date, durationMinutes, staffUserId) => {
    try {
      setIsLoading(true);
      const response = await getAvailableSlots(formatDate(date), durationMinutes, staffUserId);
      setAvailableSlots(
        Array.isArray(response.data?.data) ? response.data.data : Array.isArray(response.data) ? response.data : []
      );
    } catch (error) {
      toast.error('Error al cargar horarios disponibles');
      setAvailableSlots([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const loadStaff = async () => {
      try {
        const res = await getPublicBookingStaff();
        const data = res.data?.data ?? [];
        setStaffList(Array.isArray(data) ? data : []);
      } catch {
        toast.error('Error al cargar el equipo disponible');
      }
    };
    loadStaff();
  }, []);

  useEffect(() => {
    const loadServices = async () => {
      try {
        const response = await getPublicServices();
        const data = response.data?.data ?? (Array.isArray(response.data) ? response.data : []);
        setServices(Array.isArray(data) ? data : []);
      } catch {
        toast.error('Error al cargar servicios');
      }
    };
    loadServices();
  }, []);

  useEffect(() => {
    if (step !== 4 || !selectedDate || !selectedStaff || totalDurationMinutes <= 0) {
      if (step !== 4) setAvailableSlots([]);
      return;
    }
    fetchSlots(selectedDate, totalDurationMinutes, selectedStaff.id);
  }, [step, selectedDate, selectedStaff, totalDurationMinutes, fetchSlots]);

  const selectStaff = (s) => {
    setSelectedStaff(s);
    setSelectedServiceIds([]);
    setSelectedDate(null);
    setSelectedSlot(null);
    setStep(2);
  };

  const goToServicesNext = () => {
    if (selectedServiceIds.length === 0) {
      toast.error('Selecciona al menos un servicio');
      return;
    }
    setStep(3);
  };

  const handleDateSelect = (date) => {
    if (!selectedStaff) {
      toast.error('Selecciona primero al profesional');
      return;
    }
    if (selectedServiceIds.length === 0) {
      toast.error('Selecciona al menos un servicio');
      return;
    }
    setSelectedDate(date);
    setSelectedSlot(null);
    setStep(4);
  };

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot);
    setStep(5);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.first_name || !formData.paternal_surname || !formData.phone) {
      toast.error('Por favor completa los campos obligatorios');
      return;
    }

    if (formData.phone.length !== 10) {
      toast.error('El teléfono debe tener exactamente 10 dígitos');
      return;
    }

    if (!selectedStaff || !selectedSlot) {
      toast.error('Faltan datos de la reserva');
      return;
    }

    setIsLoading(true);
    try {
      await publicBookAppointment({
        first_name: formData.first_name,
        paternal_surname: formData.paternal_surname,
        maternal_surname: formData.maternal_surname || null,
        phone: formData.phone,
        email: formData.email || null,
        staff_user_id: selectedStaff.id,
        date: selectedSlot.date,
        start_time: selectedSlot.start_time,
        end_time: selectedSlot.end_time,
        reason: formData.reason || null,
        service_ids: selectedServiceIds.map((id) => Number(id)),
      });

      setBookingComplete(true);
      toast.success('¡Cita agendada exitosamente!');
    } catch (error) {
      let errorMessage = 'Error al agendar la cita';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.errors) {
        const firstError = Object.values(error.response.data.errors)[0];
        errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (bookingComplete) {
    return (
      <div className="min-h-screen bg-[#FDFCF8] flex flex-col">
        <div className="flex-1 flex items-center justify-center p-4">
          <Card className="w-full max-w-md border-slate-100 shadow-lg">
            <CardContent className="pt-8 pb-8 text-center">
              <div className="w-16 h-16 bg-[#588157] rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-[#2B2D42] font-['Manrope'] mb-2">¡Cita Confirmada!</h2>
              <p className="text-[#5D5C61] mb-6">Tu cita ha sido agendada exitosamente.</p>

              <div className="bg-[#F4F1EA] rounded-lg p-4 text-left mb-6 space-y-2">
                {selectedStaff && (
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-[#335C67]" />
                    <span className="text-sm text-[#2B2D42] font-medium">{selectedStaff.name}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-[#335C67]" />
                  <span className="font-medium text-[#2B2D42]">{selectedDate && formatDisplayDate(selectedDate)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-[#335C67]" />
                  <span className="text-[#5D5C61]">
                    {selectedSlot?.start_time} - {selectedSlot?.end_time}
                  </span>
                </div>
              </div>

              <p className="text-sm text-[#8D99AE] mb-4">Recibirás un recordatorio por WhatsApp antes de tu cita.</p>

              <Button
                className="w-full bg-[#335C67] hover:bg-[#244048] text-white rounded-full"
                onClick={() => {
                  window.location.href = '/portal';
                }}
              >
                Acceder a mi portal
              </Button>
            </CardContent>
          </Card>
        </div>
        <footer className="py-4 px-6 border-t border-slate-200 bg-white">
          <div className="max-w-7xl mx-auto text-center">
            <p className="text-sm text-[#8D99AE]">
              Desarrollado por{' '}
              <a
                href="https://agsoftweb.com.mx"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#335C67] hover:text-[#244048] font-medium transition-colors"
              >
                Agsoftweb
              </a>
            </p>
          </div>
        </footer>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFCF8]">
      <header className="bg-white border-b border-slate-100 py-4 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#335C67] rounded-xl flex items-center justify-center">
              <Scissors className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-lg text-[#2B2D42] font-['Manrope']">Reserveo</h1>
              <p className="text-xs text-[#8D99AE]">Agendar Cita</p>
            </div>
          </div>
          <Link to="/login" className="text-sm text-[#335C67] hover:text-[#244048] font-medium">
            Iniciar sesión
          </Link>
        </div>
      </header>

      <div className="max-w-4xl mx-auto p-6">
        <div className="flex items-center justify-center gap-2 mb-8">
          {Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1).map((s) => (
            <div
              key={s}
              className={`w-3 h-3 rounded-full transition-all ${step >= s ? 'bg-[#335C67]' : 'bg-slate-200'}`}
            />
          ))}
        </div>

        {/* 1: Profesional */}
        {step === 1 && (
          <div className="animate-fade-in">
            <h2 className="text-2xl font-bold text-[#2B2D42] font-['Manrope'] text-center mb-2">Elige a tu profesional</h2>
            <p className="text-[#5D5C61] text-center mb-8">Los horarios disponibles dependen de quien te atenderá</p>

            {staffList.length === 0 ? (
              <p className="text-center text-[#8D99AE]">No hay profesionales disponibles en este momento.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl mx-auto">
                {staffList.map((s) => (
                  <Card
                    key={s.id}
                    className={`cursor-pointer transition-all border-slate-100 hover:border-[#335C67]/40 hover:shadow-md ${
                      selectedStaff?.id === s.id ? 'border-[#335C67] bg-[#335C67]/5' : ''
                    }`}
                    onClick={() => selectStaff(s)}
                    data-testid={`booking-staff-${s.id}`}
                  >
                    <CardContent className="p-5 flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-[#335C67]/10 flex items-center justify-center">
                        <User className="w-6 h-6 text-[#335C67]" />
                      </div>
                      <div>
                        <p className="font-semibold text-[#2B2D42]">{s.name}</p>
                        <p className="text-xs text-[#8D99AE]">
                          {Array.isArray(s.service_ids) && s.service_ids.length > 0
                            ? 'Servicios específicos'
                            : 'Todos los servicios'}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* 2: Servicios */}
        {step === 2 && (
          <div className="animate-fade-in max-w-xl mx-auto">
            <Button variant="ghost" className="mb-4" onClick={() => setStep(1)}>
              <ChevronLeft className="w-4 h-4 mr-1" />
              Cambiar profesional
            </Button>
            <h2 className="text-2xl font-bold text-[#2B2D42] font-['Manrope'] text-center mb-2">Selecciona servicios</h2>
            <p className="text-[#5D5C61] text-center mb-2">
              Con <span className="font-medium text-[#2B2D42]">{selectedStaff?.name}</span>
            </p>
            <p className="text-sm text-[#8D99AE] text-center mb-6">Puedes elegir uno o varios. La duración total define los huecos en la agenda.</p>

            {bookableServices.length === 0 ? (
              <p className="text-center text-[#8D99AE]">No hay servicios disponibles para este profesional.</p>
            ) : (
              <>
                <div className="flex flex-wrap gap-3 justify-center mb-4">
                  {bookableServices.map((svc) => {
                    const idStr = String(svc.id);
                    const checked = selectedServiceIds.includes(idStr);
                    return (
                      <label
                        key={svc.id}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                          checked ? 'border-[#335C67] bg-[#335C67]/5' : 'border-slate-200 hover:border-[#335C67]/30'
                        } cursor-pointer`}
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(value) => {
                            setSelectedServiceIds((prev) => {
                              if (value) {
                                return prev.includes(idStr) ? prev : [...prev, idStr];
                              }
                              return prev.filter((x) => x !== idStr);
                            });
                          }}
                        />
                        <span className="text-sm text-[#2B2D42]">
                          {svc.name} ({svc.duration_minutes} min)
                        </span>
                      </label>
                    );
                  })}
                </div>
                <p className="text-xs text-[#8D99AE] text-center mb-6">Duración total: {totalDurationMinutes} min</p>
                <Button
                  className="w-full bg-[#335C67] hover:bg-[#244048] text-white rounded-full"
                  onClick={goToServicesNext}
                  disabled={totalDurationMinutes <= 0}
                >
                  Continuar a elegir fecha
                </Button>
              </>
            )}
          </div>
        )}

        {/* 3: Fecha */}
        {step === 3 && (
          <div className="animate-fade-in">
            <Button variant="ghost" className="mb-4" onClick={() => setStep(2)}>
              <ChevronLeft className="w-4 h-4 mr-1" />
              Cambiar servicios
            </Button>
            <h2 className="text-2xl font-bold text-[#2B2D42] font-['Manrope'] text-center mb-2">Selecciona una fecha</h2>
            <p className="text-[#5D5C61] text-center mb-8">Elige el día; después verás solo horarios libres para {selectedStaff?.name}</p>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3">
              {getDates().map((date) => {
                const isToday = formatDate(date) === formatDate(new Date());
                return (
                  <Card
                    key={formatDate(date)}
                    className={`cursor-pointer transition-all hover:shadow-md ${
                      selectedDate && formatDate(selectedDate) === formatDate(date)
                        ? 'border-[#335C67] bg-[#335C67]/5'
                        : 'border-slate-100 hover:border-[#335C67]/30'
                    }`}
                    onClick={() => handleDateSelect(date)}
                    data-testid={`date-${formatDate(date)}`}
                  >
                    <CardContent className="p-4 text-center">
                      <p className="text-xs text-[#8D99AE] uppercase">
                        {date.toLocaleDateString('es-MX', { weekday: 'short' })}
                      </p>
                      <p className="text-2xl font-bold text-[#2B2D42] my-1">{date.getDate()}</p>
                      <p className="text-xs text-[#5D5C61]">{date.toLocaleDateString('es-MX', { month: 'short' })}</p>
                      {isToday && (
                        <Badge className="mt-2 bg-[#E09F3E] text-xs">Hoy</Badge>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* 4: Hora */}
        {step === 4 && (
          <div className="animate-fade-in">
            <Button variant="ghost" className="mb-4" onClick={() => setStep(3)}>
              <ChevronLeft className="w-4 h-4 mr-1" />
              Cambiar fecha
            </Button>

            <h2 className="text-2xl font-bold text-[#2B2D42] font-['Manrope'] text-center mb-2">Selecciona un horario</h2>
            <p className="text-[#5D5C61] text-center mb-1">{selectedDate && formatDisplayDate(selectedDate)}</p>
            <p className="text-sm text-[#8D99AE] text-center mb-2">Con {selectedStaff?.name}</p>
            <p className="text-xs text-[#335C67]/80 text-center mb-8">
              Horarios consultados en el servidor según tu duración ({totalDurationMinutes} min)
            </p>

            {isLoading ? (
              <div className="text-center py-12">
                <div className="w-8 h-8 border-4 border-[#335C67] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <p className="text-[#5D5C61]">Cargando horarios...</p>
              </div>
            ) : availableSlots.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-w-2xl mx-auto">
                {availableSlots.map((slot, index) => (
                  <Button
                    key={`${slot.start_time}-${index}`}
                    variant="outline"
                    className={`h-auto py-4 ${
                      selectedSlot?.start_time === slot.start_time && selectedSlot?.date === slot.date
                        ? 'border-[#335C67] bg-[#335C67] text-white'
                        : 'border-slate-200 hover:border-[#335C67]'
                    }`}
                    onClick={() => handleSlotSelect(slot)}
                    data-testid={`slot-${slot.start_time}`}
                  >
                    <Clock className="w-4 h-4 mr-2" />
                    {slot.start_time}
                  </Button>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Calendar className="w-12 h-12 text-[#8D99AE] mx-auto mb-4" />
                <h3 className="text-lg font-medium text-[#2B2D42]">Sin disponibilidad</h3>
                <p className="text-[#5D5C61] mt-1">No hay horarios disponibles para esta fecha con la duración elegida</p>
                <Button variant="outline" className="mt-4" onClick={() => setStep(3)}>
                  Elegir otra fecha
                </Button>
              </div>
            )}
          </div>
        )}

        {/* 5: Datos */}
        {step === 5 && (
          <div className="animate-fade-in max-w-md mx-auto">
            <Button variant="ghost" className="mb-4" onClick={() => setStep(4)}>
              <ChevronLeft className="w-4 h-4 mr-1" />
              Cambiar horario
            </Button>

            <Card className="border-slate-100">
              <CardHeader>
                <CardTitle className="text-xl font-['Manrope']">Tus datos</CardTitle>
                <CardDescription>Completa tu información para confirmar la cita</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="bg-[#F4F1EA] rounded-lg p-4 mb-6 space-y-2">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-[#335C67]" />
                    <span className="text-sm font-medium text-[#2B2D42]">{selectedStaff?.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-[#335C67]" />
                    <span className="font-medium text-[#2B2D42]">{selectedDate && formatDisplayDate(selectedDate)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-[#335C67]" />
                    <span className="text-[#5D5C61]">
                      {selectedSlot?.start_time} - {selectedSlot?.end_time}
                    </span>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <User className="w-4 h-4 text-[#8D99AE]" />
                      Nombre(s) *
                    </Label>
                    <Input
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      placeholder="Juan Carlos"
                      data-testid="booking-first-name"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Apellido Paterno *</Label>
                      <Input
                        value={formData.paternal_surname}
                        onChange={(e) => setFormData({ ...formData, paternal_surname: e.target.value })}
                        placeholder="Pérez"
                        data-testid="booking-paternal-surname"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Apellido Materno</Label>
                      <Input
                        value={formData.maternal_surname}
                        onChange={(e) => setFormData({ ...formData, maternal_surname: e.target.value })}
                        placeholder="García"
                        data-testid="booking-maternal-surname"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-[#8D99AE]" />
                      Teléfono (10 dígitos) *
                    </Label>
                    <Input
                      value={formData.phone}
                      onChange={handlePhoneChange}
                      placeholder="8123456789"
                      data-testid="booking-phone"
                      required
                      maxLength={10}
                      type="tel"
                    />
                    <p className="text-xs text-[#8D99AE]">
                      Ingresa solo los 10 dígitos (sin LADA). Recibirás confirmación y recordatorios
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-[#8D99AE]" />
                      Correo (opcional)
                    </Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="correo@ejemplo.com"
                      data-testid="booking-email"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Motivo de reserva (opcional)</Label>
                    <Input
                      value={formData.reason}
                      onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                      placeholder="Ej: cita por primera vez, mantenimiento, etc."
                      data-testid="booking-reason"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-[#335C67] hover:bg-[#244048] text-white rounded-full h-12"
                    disabled={isLoading}
                    data-testid="booking-submit"
                  >
                    {isLoading ? 'Confirmando...' : 'Confirmar Cita'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      <footer className="mt-auto py-4 px-6 border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-sm text-[#8D99AE]">
            Desarrollado por{' '}
            <a
              href="https://agsoftweb.com.mx"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#335C67] hover:text-[#244048] font-medium transition-colors"
            >
              Agsoftweb
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
}
